/* eslint-disable no-param-reassign */
import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  EMSPPreauthAPIResponse,
  EMSPPreauthInitialProperties,
  EMSPPreauthInterface,
  RequestEMSPPreauthInterface,
} from '../../interface/emsp-preauth.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import FormatUtcString from '../../shared-functions/FormatUtcString';
import emspPreauthApi from '../../utils/api/v2/emsp-preauth';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Thunk to fetch end consumer payment history records.
 * @param {RequestEMSPPreauthInterface} data - Query payload for fetching payment history records.
 * @returns {Promise<EMSPPreauthAPIResponse>} Promise resolving to preauth API response.
 */
export const fetchConsumerPaymentHistory = createAsyncThunk<
  EMSPPreauthAPIResponse,
  RequestEMSPPreauthInterface
>(
  'endConsumerPaymentHistory/fetchConsumerPaymentHistory',
  async (data: RequestEMSPPreauthInterface, { dispatch }) => {
    try {
      const response = await emspPreauthApi.fetchPreauthList(removeEmptyKeys(data));
      const rawResults = response?.data?.results || [];
      const results = rawResults.map((item: EMSPPreauthInterface) => ({
        ...item,
        id: item._id,
        date: ConvertUTCtoDate(item.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(item.createdAt)),
      }));

      return {
        results,
        page: response?.data?.page || 1,
        limit: response?.data?.limit || 20,
        totalPages: response?.data?.totalPages || 0,
        totalResults: response?.data?.totalResults || results.length,
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch payment history records',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to refresh end consumer payment history records without triggering main loader.
 * @param {RequestEMSPPreauthInterface} data - Query payload for refreshing payment history records.
 * @returns {Promise<EMSPPreauthAPIResponse>} Promise resolving to preauth API response.
 */
export const refreshConsumerPaymentHistory = createAsyncThunk<
  EMSPPreauthAPIResponse,
  RequestEMSPPreauthInterface
>(
  'endConsumerPaymentHistory/refreshConsumerPaymentHistory',
  async (data: RequestEMSPPreauthInterface, { dispatch }) => {
    try {
      const response = await emspPreauthApi.fetchPreauthList(removeEmptyKeys(data));
      const rawResults = response?.data?.results || [];
      const results = rawResults.map((item: EMSPPreauthInterface) => ({
        ...item,
        id: item._id,
        date: ConvertUTCtoDate(item.createdAt),
        time: FormatUtcString(ConvertUTCtoHHMM(item.createdAt)),
      }));

      return {
        results,
        page: response?.data?.page || 1,
        limit: response?.data?.limit || 20,
        totalPages: response?.data?.totalPages || 0,
        totalResults: response?.data?.totalResults || results.length,
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to refresh payment history records',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

/**
 * Thunk to fetch filter options for end consumer payment history list.
 * @returns {Promise<CommonFilterProperties[]>} Promise resolving to common filter properties.
 */
export const fetchConsumerPaymentHistoryFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'endConsumerPaymentHistory/fetchConsumerPaymentHistoryFilters',
  async (_, { dispatch }) => {
    try {
      const response = await emspPreauthApi.fetchPreauthListFilters();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch filters',
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      return [];
    }
  },
);

/**
 * Default available filter definitions for end consumer payment history.
 */
const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'Status',
    key: 'status',
    type: filterType.SELECT_FIELD,
    value: [],
    listOfValue: [
      { label: 'Reserved', value: 'RESERVED' },
      { label: 'Linked', value: 'LINKED' },
      { label: 'Captured', value: 'CAPTURED' },
      { label: 'Released', value: 'RELEASED' },
      { label: 'Orphaned', value: 'ORPHANED' },
      { label: 'Pending', value: 'PENDING' },
      { label: 'Failed', value: 'FAILED' },
      { label: 'Expired', value: 'EXPIRED' },
    ],
    applyFilter: false,
    enable: true,
  },
  {
    name: 'Payment Method',
    key: 'paymentMethod',
    type: filterType.SELECT_FIELD,
    value: [],
    listOfValue: [
      { label: 'Card', value: 'card' },
      { label: 'Wallet', value: 'wallet' },
      { label: 'Apple Pay', value: 'applepay' },
    ],
    applyFilter: false,
    enable: true,
  },
];

/**
 * Initial state for End Consumer Payment History slice.
 */
const initialState: EMSPPreauthInitialProperties = {
  preauths: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

/**
 * Redux slice for End Consumer Payment History management.
 */
export const endConsumerPaymentHistorySlice = createSlice({
  name: 'endConsumerPaymentHistory',
  initialState,
  reducers: {
    /**
     * Update End Consumer Payment History filters.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     * @param {PayloadAction<FilterUpdateProperties>} action - Payload containing filter update payload.
     */
    updateConsumerPaymentHistoryFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Reset filter applied status flag.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     */
    disableConsumerPaymentHistoryFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * Set table sort criteria.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     * @param {PayloadAction<{ sortField: null}>} action - Sort metadata.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * Clear all End Consumer Payment History state data.
     * @param {EMSPPreauthInitialProperties} state - Current Redux state.
     */
    clearConsumerPaymentHistoryData: (state) => {
      Object.assign(state, initialState);
    },
  },
  /**
   * Extra reducers for async thunk actions.
   * @param {ActionReducerMapBuilder<EMSPPreauthInitialProperties>} builder - Redux builder.
   */
  extraReducers: (builder: ActionReducerMapBuilder<EMSPPreauthInitialProperties>) => {
    builder
      .addCase(fetchConsumerPaymentHistory.pending, (state: EMSPPreauthInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchConsumerPaymentHistory.fulfilled,
        (
          state: EMSPPreauthInitialProperties,
          action: PayloadAction<EMSPPreauthAPIResponse>,
        ) => {
          state.loading = false;
          state.preauths = action.payload.results;
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchConsumerPaymentHistory.rejected, (state: EMSPPreauthInitialProperties) => {
        state.loading = false;
      });

    builder.addCase(
      refreshConsumerPaymentHistory.fulfilled,
      (
        state: EMSPPreauthInitialProperties,
        action: PayloadAction<EMSPPreauthAPIResponse>,
      ) => {
        state.preauths = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );

    builder
      .addCase(fetchConsumerPaymentHistoryFilters.pending, (state: EMSPPreauthInitialProperties) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchConsumerPaymentHistoryFilters.fulfilled,
        (
          state: EMSPPreauthInitialProperties,
          action: PayloadAction<CommonFilterProperties[]>,
        ) => {
          state.filterListLoading = false;
          if (action.payload && action.payload.length > 0) {
            state.filters = [...state.filters, ...action.payload].filter(
              (object, index, self) => index === self.findIndex((t) => t.key === object.key),
            );
          }
        },
      )
      .addCase(fetchConsumerPaymentHistoryFilters.rejected, (state: EMSPPreauthInitialProperties) => {
        state.filterListLoading = false;
      });
  },
});

export const {
  updateConsumerPaymentHistoryFilters,
  disableConsumerPaymentHistoryFilterApplied,
  setSortData,
  clearConsumerPaymentHistoryData,
} = endConsumerPaymentHistorySlice.actions;

export default endConsumerPaymentHistorySlice.reducer;
